import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Modal,
  Backdrop,
  Fade,
  Divider,
  Stack,
  Button,
  Checkbox,
} from "@mui/material";

import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  setLockAccessCodes,
  pushToHistoryAction,
  setLockEmailPermission,
  setIsSavingWheel,
  setIsLocked,
} from "../../redux/reducers/segmentBuilder";
import {
  getLockAccessCodePermission,
  getLockAccessCodes,
  getLockEmailPermission,
} from "../../redux/selectors/segmentBuilderSelectors";
import generateStrongPassword from "./generatePassword";
import LockSettingModalHeader from "./components/LockSettingModalHeader";
import PasswordAccordion from "./components/PasswordAccordion";
import EmailAccordion from "./components/EmailAccordion";
import EmailListing from "./components/EmailListing";
import { colors } from "../../utils";
import { useAddEmails, useUpdateWheelV2 } from "../../services/mutations";
import useSegmentSettings from "../../hooks/useSegmentSetting";
import { useGetEmailPermissions } from "../../services/queries";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: colors.white,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4),
    width: "60%",
    minWidth: "700px",
    maxHeight: "80%",
    borderRadius: 10,
    position: "relative",
    zIndex: 1300,
  },
}));

const VIEWS = {
  EMAIL_LIST: "emailList",
  INPUT_VIEW: "default",
};

const LockSettingsModal = ({ isOpen, handleClose, wheelId }) => {
  const lockAccessCodes = useSelector(getLockAccessCodes);
  const lockEmailPermission = useSelector(getLockEmailPermission);
  const lockAccessCodePermission = useSelector(getLockAccessCodePermission);
  const dispatch = useDispatch();
  const classes = useStyles();
  const [password, setPassword] = useState("");
  const [view, setView] = useState(VIEWS.INPUT_VIEW);
  const [emailInput, setEmailInput] = useState([]);
  const [keys, setKeys] = useState(lockAccessCodes);
  const [searchQuery, setSearchQuery] = useState("");
  const { mutate: addEmails, isLoading } = useAddEmails();
  const { data, fetchNextPage, hasNextPage, isFetching, isError, error } =
    useGetEmailPermissions(wheelId, searchQuery);

  // Refetch emails when returning to input view from email listing
  useEffect(() => {
    if (view === VIEWS.INPUT_VIEW) {
      // Temporarily clear search to refetch all emails without search filter
      setSearchQuery("");
    }
  }, [view]);

  const [isPasswordEnabled, setIsPasswordEnabled] = useState(
    lockAccessCodePermission
  );
  const [isEmailEnabled, setIsEmailEnabled] = useState(lockEmailPermission);
  const updateWheel = useUpdateWheelV2(wheelId);
  const generateKeyPress = () => {
    setPassword(generateStrongPassword());
  };

  const wheelInformation = useSelector((state) => state.segmentBuilder);
  const wheelWallpaperSettings = useSelector(
    (state) => state.wheelWallpaperReducer
  );

  const { generatePayload } = useSegmentSettings() || {};

  const addKeyToList = () => {
    const trimmedPassword = password.trim();
    if (
      !trimmedPassword ||
      keys.some((key) => key.code === trimmedPassword) ||
      keys.length >= 10
    ) {
      toast.error("Invalid or duplicate Password.");
      setPassword("");
      return;
    }
    setKeys([...keys, { code: trimmedPassword, is_active: true }]);
    setPassword("");
  };

  const handleApplySettings = () => {
    // Check if either password (keys) or email permission (emails) is enabled
    if (keys.length === 0 && emailInput.length === 0) {
      toast.error(
        "Please add at least one key or user permission to apply settings."
      );
      return; // Prevent applying settings if neither is enabled
    }
    dispatch(setLockEmailPermission(isEmailEnabled));
    dispatch(
      setLockAccessCodes({ access_codes: keys, is_active: isPasswordEnabled })
    );
    dispatch(pushToHistoryAction());

    // Create updated wheel information with current local state values
    const currentWheelInfo =
      wheelInformation?.history[wheelInformation?.currentIndex] || {};
    const updatedWheelInfo = {
      ...currentWheelInfo,
      lock_type: {
        ...currentWheelInfo.lock_type,
        access_code_lock: {
          access_codes: keys,
          is_active: isPasswordEnabled,
        },
        permission_lock: {
          ...currentWheelInfo.lock_type?.permission_lock,
          is_active: isEmailEnabled,
          // emails: emailInput,
        },
      },
    };

    let payload = generatePayload(updatedWheelInfo, wheelWallpaperSettings);
    payload = wheelId ? { ...payload, id: Number(wheelId) } : { ...payload };

    dispatch(setIsSavingWheel(true));
    updateWheel.mutate(payload, {
      onSuccess: () => {
        dispatch(setIsLocked(true));
        toast.success("Lock settings updated!");
        handleClose();
        setIsSavingWheel(false);
      },
      onError: (err) => {
        toast.error(err?.message);
        dispatch(setIsSavingWheel(false));
      },
    });
  };

  const onUpdateButtonPress = () => {
    addEmails(
      { wheelId, emails: emailInput },
      {
        onSuccess: () => {
          toast.success("Emails added successfully!");
        },
        onError: (error) => {
          toast.error("Error adding emails:", error);
        },
      }
    );
  };

  const toggleIsActive = (index) => {
    setKeys((prevKeys) =>
      prevKeys.map((keyItem, keyIdx) =>
        keyIdx === index
          ? { ...keyItem, is_active: !keyItem.is_active }
          : keyItem
      )
    );
  };

  return (
    <Modal
      className={classes.modal}
      open={isOpen}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={isOpen}>
        <div className={classes.paper}>
          {view === VIEWS.INPUT_VIEW ? (
            <>
              <LockSettingModalHeader
                showBackButton={view === VIEWS.EMAIL_LIST}
                onBackIconClick={() => setView(VIEWS.INPUT_VIEW)}
              />
              <Divider sx={{ mt: 2, mb: 2 }} variant="middle" />
              <div style={{ overflowY: "auto", maxHeight: "60vh" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    marginBottom: 20,
                  }}
                >
                  <Checkbox
                    style={{ marginTop: 10 }}
                    checked={isPasswordEnabled}
                    onChange={(e) => setIsPasswordEnabled(e.target.checked)}
                  />
                  <PasswordAccordion
                    password={password}
                    setPassword={setPassword}
                    generateKeyPress={generateKeyPress}
                    addKeyToList={addKeyToList}
                    keys={keys}
                    setKeys={setKeys}
                    toggleIsActive={toggleIsActive}
                  />
                </div>
                <div
                  style={{ display: "flex", alignItems: "flex-start", gap: 10 }}
                >
                  <Checkbox
                    style={{ marginTop: 10 }}
                    checked={isEmailEnabled}
                    onChange={(e) => setIsEmailEnabled(e.target.checked)}
                  />
                  <EmailAccordion
                    onChange={(emailsArray) => setEmailInput(emailsArray)}
                    emails={emailInput}
                    onUpdateButtonPress={onUpdateButtonPress}
                    onUsersIconPress={() => setView(VIEWS.EMAIL_LIST)}
                    isLoading={isLoading}
                    isFetchingEmails={isFetching}
                    emailCount={
                      data?.pages.flatMap((page) => page.results)?.length || 0
                    }
                  />
                </div>
              </div>
              <Divider sx={{ mt: 2 }} variant="middle" />
              <Stack
                direction="row"
                sx={{ mt: 2 }}
                gap={1}
                justifyContent="flex-end"
              >
                <Button
                  sx={{
                    color: colors.textSecondary,
                    textTransform: "none",
                    fontFamily: "DM Sans",
                  }}
                  onClick={handleClose}
                  variant="text"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleApplySettings}
                  variant="text"
                  sx={{
                    color: colors.PrimaryPurple,
                    textTransform: "none",
                    fontFamily: "DM Sans",
                    fontWeight: "500",
                    fontSize: 14,
                  }}
                  disabled={keys.length === 0 && emailInput.length === 0}
                >
                  Apply Settings
                </Button>
              </Stack>
            </>
          ) : (
            <EmailListing
              onGoBackPress={() => setView(VIEWS.INPUT_VIEW)}
              wheelId={wheelId}
              data={data}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetching={isFetching}
              isError={isError}
              error={error}
              onSearch={setSearchQuery}
            />
          )}
        </div>
      </Fade>
    </Modal>
  );
};

export default LockSettingsModal;
